import { useState, useRef, useCallback, useEffect } from 'react';
import { preprocessReceipt, convertPdfToJpeg } from '../utils/image_processor';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { CURRENCIES, CATEGORIES } from '../utils/constants';
import { normalizeCategory } from '../utils/categoryHelpers';
import { trackScanReceipt } from '../analytics';

const scanReceiptFn = httpsCallable(functions, 'scanReceipt', { timeout: 60000 });

export function useReceiptScanner({ checkLimit, showUpgradePrompt, refreshUsage }) {
  const videoRef          = useRef();
  const canvasRef         = useRef();
  const streamRef         = useRef();
  const torchDeviceIdRef  = useRef(null);
  const cropImgRef        = useRef(null);

  const [showCamera,     setShowCamera]     = useState(false);
  const [cameraError,    setCameraError]    = useState(null);
  const [videoReady,     setVideoReady]     = useState(false);
  const [torchOn,        setTorchOn]        = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [analyzing,      setAnalyzing]      = useState(false);
  const [pendingScan,    setPendingScan]    = useState(null); // { dataUrl, base64, mimeType }
  const [crop,           setCrop]           = useState(null);
  const [capturedImage,  setCapturedImage]  = useState(null);
  const [extractError,   setExtractError]   = useState(null);
  const [newExpenseForm, setNewExpenseForm] = useState(null);

  // Release stream when camera is hidden
  useEffect(() => {
    if (!showCamera && streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, [showCamera]);

  // Release on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const processDataUrl = useCallback(async (dataUrl, mimeType) => {
    const scanCheck = checkLimit('scansPerMonth');
    if (!scanCheck.allowed) { showUpgradePrompt(scanCheck); return; }
    try {
      let finalDataUrl  = dataUrl;
      let finalMimeType = mimeType;
      const isPdf = mimeType === 'application/pdf';
      if (isPdf) {
        try {
          finalDataUrl  = await convertPdfToJpeg(dataUrl);
          finalMimeType = 'image/jpeg';
        } catch (err) {
          console.warn('PDF conversion failed, sending PDF directly:', err);
        }
      }
      if (finalMimeType !== 'application/pdf') {
        try {
          const { dataURL: processed } = await preprocessReceipt(finalDataUrl, {
            outputQuality: isPdf ? 0.80 : 0.72,
            maxWidth:  isPdf ? 2000 : 800,
            maxHeight: isPdf ? 3000 : 1200,
            grayscale: true,
            deskew: false,
            // deskew: !isPdf,
            autoCrop: true,
          });
          finalDataUrl = processed;
        } catch (err) {
          console.warn('preprocessReceipt failed, using original:', err);
        }
      }
      const base64 = finalDataUrl.split(',')[1];
      setCapturedImage(finalMimeType !== 'application/pdf' ? finalDataUrl : null);
      setPendingScan({ dataUrl: finalDataUrl, base64, mimeType: finalMimeType });
      setExtractError(null);
      setNewExpenseForm(null);
    } catch (err) {
      console.error('Failed to preprocess image:', err);
      setExtractError('Failed to prepare image. Please try again.');
    }
  }, [checkLimit, showUpgradePrompt]);

  const doScan = useCallback(async (base64, finalMimeType, finalDataUrl) => {
    setPendingScan(null);
    setCrop(null);
    setAnalyzing(true);
    setExtractError(null);
    try {
      const result = await scanReceiptFn({ base64Image: base64, mimeType: finalMimeType });
      const data = result.data;
      await refreshUsage();
      trackScanReceipt();
      const detectedCur = CURRENCIES.find(c => c.code === (data.currency || 'USD').toUpperCase());
      const matchedCatLabel = CATEGORIES.find(cat =>
        cat.toLowerCase().includes((data.category || '').toLowerCase().split(' ')[0])
      );
      const matchedCat = normalizeCategory(matchedCatLabel || data.category || 'other');
      console.log("data:", data)
      setNewExpenseForm({
        vendor:     data.vendor     || '',
        category:   matchedCat,
        subtotal:   data.subtotal  || '',
        tax:        data.tax       || '',
        tip:        data.tip       || '',
        total:      data.total     || '',
        currency:   detectedCur?.code || 'USD',
        date:       data.date       || new Date().toISOString().split('T')[0],
        start_date: data.start_date || null,
        end_date:   data.end_date   || null,
        location:   data.location   || null,
        notes:      '',
        status:     'success',
        imagePreview: finalDataUrl,
        mimeType:     finalMimeType,
      });
    } catch (err) {
      console.error('Failed to extract receipt data:', err);
      if (err?.code === 'functions/resource-exhausted') {
        showUpgradePrompt(checkLimit('scansPerMonth'));
      } else if (err?.code === 'functions/unauthenticated') {
        setExtractError('Sign in to scan receipts.');
      } else {
        setExtractError('Failed to extract receipt data. Please try again or enter manually.');
      }
    } finally {
      setAnalyzing(false);
    }
  }, [checkLimit, showUpgradePrompt, refreshUsage]);

  const handleImageFile = useCallback(async (source, mimeType = 'image/jpeg') => {
    if (typeof source === 'string') { await processDataUrl(source, mimeType); return; }
    if (!source || (!(source instanceof File) && !(source instanceof Blob))) {
      setExtractError('Invalid file selected. Please choose an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload  = (ev) => {
      const dataUrl = ev.target.result;
      if (!dataUrl || typeof dataUrl !== 'string') { setExtractError('Failed to read file. Please try again.'); return; }
      processDataUrl(dataUrl, source.type || mimeType);
    };
    reader.onerror = () => setExtractError('Failed to read file. Please try again.');
    reader.readAsDataURL(source);
  }, [processDataUrl]);

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      setVideoReady(false);
      setShowCamera(true);

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

      const probe = await navigator.mediaDevices.getUserMedia({
        video: isIOS ? { facingMode: 'environment' } : true,
      });
      probe.getTracks().forEach(t => t.stop());

      if (!isIOS && !torchDeviceIdRef.current) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput').reverse();
        for (const cam of cameras) {
          try {
            const s     = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: cam.deviceId } } });
            const track = s.getVideoTracks()[0];
            const hasTorch = track?.getCapabilities?.().torch === true;
            s.getTracks().forEach(t => t.stop());
            if (hasTorch) { torchDeviceIdRef.current = cam.deviceId; break; }
          } catch {}
        }
      }

      const videoConstraint = torchDeviceIdRef.current
        ? { deviceId: { exact: torchDeviceIdRef.current }, width: { ideal: 1280 }, height: { ideal: 960 } }
        : { facingMode: { exact: 'environment' }, width: { ideal: 1280 }, height: { ideal: 960 } };

      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraint })
        .catch(() => navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } },
        }));

      streamRef.current = stream;
      setTorchSupported(!!torchDeviceIdRef.current);

      const track        = stream.getVideoTracks()[0];
      const capabilities = track?.getCapabilities?.() || {};
      if (capabilities.focusMode?.includes('continuous')) {
        try { await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch {}
      }

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(err => console.error('Video play failed:', err));
            setVideoReady(true);
          };
          // Fallback in case onloadedmetadata never fires
          setTimeout(() => { setVideoReady(v => v || true); }, 2000);
        }
      }, 100);
    } catch (err) {
      console.error('Camera access failed:', err);
      setCameraError('Camera access denied. Grant permission or use the upload button instead.');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setVideoReady(false);
    setCameraError(null);
    setTorchOn(false);
  }, []);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (err) {
      console.warn('Torch toggle failed:', err);
      setTorchSupported(false);
    }
  }, [torchOn]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    stopCamera();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    handleImageFile(dataUrl, 'image/jpeg');
  }, [stopCamera, handleImageFile]);

  // Like capturePhoto but center-crops the frame to the given w/h ratio,
  // matching what the user sees in the viewfinder.
  const capturePhotoWithRatio = useCallback((wToH) => {
    if (!videoRef.current || !canvasRef.current) return;
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    const vw = video.videoWidth, vh = video.videoHeight;
    let srcX, srcY, srcW, srcH;
    if (vw / vh > wToH) {
      srcH = vh; srcW = Math.round(vh * wToH);
      srcX = Math.round((vw - srcW) / 2); srcY = 0;
    } else {
      srcW = vw; srcH = Math.round(vw / wToH);
      srcX = 0; srcY = Math.round((vh - srcH) / 2);
    }
    canvas.width  = srcW;
    canvas.height = srcH;
    canvas.getContext('2d').drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
    stopCamera();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    handleImageFile(dataUrl, 'image/jpeg');
  }, [stopCamera, handleImageFile]);

  const retakeScan = useCallback(() => {
    setPendingScan(null);
    setCapturedImage(null);
    setCrop(null);
    setExtractError(null);
  }, []);

  const applyCropAndScan = useCallback(() => {
    if (!pendingScan) return;
    if (!crop?.width || !crop?.height || !cropImgRef.current) {
      doScan(pendingScan.base64, pendingScan.mimeType, pendingScan.dataUrl);
      return;
    }
    const img    = cropImgRef.current;
    const scaleX = img.naturalWidth  / img.width;
    const scaleY = img.naturalHeight / img.height;
    const canvas = document.createElement('canvas');
    canvas.width  = Math.round(crop.width  * scaleX);
    canvas.height = Math.round(crop.height * scaleY);
    canvas.getContext('2d').drawImage(
      img,
      Math.round(crop.x * scaleX), Math.round(crop.y * scaleY),
      canvas.width, canvas.height,
      0, 0, canvas.width, canvas.height,
    );
    const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(croppedDataUrl);
    doScan(croppedDataUrl.split(',')[1], pendingScan.mimeType, croppedDataUrl);
  }, [pendingScan, crop, doScan]);

  const resetScanState = useCallback(() => {
    setNewExpenseForm(null);
    setCapturedImage(null);
    setExtractError(null);
    setPendingScan(null);
    setCrop(null);
  }, []);

  return {
    // refs
    videoRef, canvasRef, cropImgRef,
    // camera state
    showCamera, cameraError, videoReady, torchOn, torchSupported,
    // scan state
    analyzing, pendingScan, crop, setCrop,
    capturedImage, extractError, newExpenseForm, setNewExpenseForm,
    // handlers
    startCamera, stopCamera, toggleTorch, capturePhoto, capturePhotoWithRatio,
    handleImageFile, applyCropAndScan, retakeScan, resetScanState,
  };
}
